import { useCallback } from "react";

import { cacheTiers } from "@/api/queryClient";
import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import {
  LEGACY_FILTER_PARAMS,
  type LegacyFilterParam,
} from "@/lib/utils/daylight-insight/dashboardSpec";
import { getCustomViews as getCustomViewsUrl } from "@/service/reqUrl.service";

import { unwrapRows } from "./unwrapRows";
import {
  USER_FILTER_PERF_CODE,
  type CustomViewDetails,
  type CustomViewRow,
  type CustomViewType,
  type GetCustomViewsParams,
  type SavedCustomView,
  type SavedDashboardFilters,
} from "./types";

/**
 * The user's saved preferences for one dashboard, read in a SINGLE
 * `POST /dashboard/custom-views` call (`[DayLight].[GetAppUserPerfs]`).
 *
 * The SP returns every `AppUserPerf` row saved against the dashboard, of two
 * kinds:
 *   • `PerfCode = 'CustomView'`  → a table column layout  → {@link views}
 *   • `PerfCode = 'UserFilter'`  → the dashboard's filter selections →
 *     {@link filters}
 *
 * Both are needed before the dashboard can render — the filters decide what the
 * metric calls ask for, the views decide which columns the tables show — so
 * they are fetched once, here, and shared through
 * `DashboardPerfsProvider` rather than re-fetched per table.
 */
export interface DashboardPerfs {
  /** Every saved column layout for this dashboard, ALL view types (the tables
   *  narrow to their own). `null` while the call is outstanding. */
  views: SavedCustomView[] | null;
  /** The saved filter selections, or `null` when the user has none — in which
   *  case the dashboard keeps its default filters. */
  filters: SavedDashboardFilters | null;
  loading: boolean;
  error: string | null;
  /** True once the call for the CURRENT dashboard has settled, success or
   *  failure. The page holds its data requests until this flips, so the first
   *  metric call already carries the saved filters instead of firing with the
   *  defaults and re-firing a moment later. Stays false while the hook is idle
   *  (no dashboard id yet). */
  ready: boolean;
  /** Re-read the row set — call after saving or deleting a view. */
  refetch: () => void;
}

/** A dashboard scope as a comparable key. NULL / "" / absent all mean the same
 *  thing — "not tied to a dashboard", which is All Metrics' own scope — and the
 *  id itself is compared as a string so 12 and "12" match. */
export const dashKey = (v: unknown): string | null =>
  v === null || v === undefined || v === "" ? null : String(v).trim();

/** The row's own dashboard scope. A MISSING key reads as null, not as unknown:
 *  the SP's `FOR JSON` drops NULL properties, so "no DashboardID" is exactly how
 *  an All-Metrics view (saved with `@DashboardID = NULL`) comes back. */
const DASH_KEYS = ["DashboardID", "DashboardId", "dashboardID", "dashboardId"];
const rowDash = (r: CustomViewRow): string | null => {
  const k = DASH_KEYS.find((key) => key in r);
  return k === undefined ? null : dashKey(r[k]);
};

/** `PerfDetails` as an object, whether the SP sent JSON text or a real object.
 *  Null for anything unreadable. */
function detailsObject(raw: unknown): Record<string, unknown> | null {
  let obj: unknown = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== "object") return null;
  return obj as Record<string, unknown>;
}

/** Parse a `PerfDetails` cell into the column layout we stored. Returns null for
 *  anything unreadable. */
function parseViewDetails(raw: unknown): CustomViewDetails | null {
  const d = detailsObject(raw);
  if (!d) return null;

  // An empty column list is KEPT. Dropping it here hid the view from the picker
  // entirely — which also made it undeletable, since the ✕ only exists for views
  // the picker renders. The table falls back to showing every column for a view
  // that resolves to none, so an empty one is harmless as well as removable.
  const columns = Array.isArray(d.columns)
    ? d.columns.filter((c): c is string => typeof c === "string")
    : [];

  const sortRaw = d.sort as { col?: unknown; dir?: unknown } | null | undefined;
  const sort =
    sortRaw && typeof sortRaw.col === "string"
      ? { col: sortRaw.col, dir: sortRaw.dir === "asc" ? ("asc" as const) : ("desc" as const) }
      : null;

  return {
    viewType: (typeof d.viewType === "string" ? d.viewType : "branch") as CustomViewType,
    columns,
    sort,
    isDefault: d.isDefault === true,
  };
}

/** One saved filter value list. The SP's JSON carries arrays, but a
 *  comma-separated string is accepted too — that's the shape the metric APIs
 *  take, so it is the likely spelling if the filter is ever saved from the
 *  request body rather than the UI selection. */
const valueList = (raw: unknown): string[] => {
  const out = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? raw.split(",")
      : [];
  return out
    .map((v) => (typeof v === "string" || typeof v === "number" ? String(v).trim() : ""))
    .filter((v) => v.length > 0);
};

const text = (raw: unknown): string | null => {
  const v = typeof raw === "string" ? raw.trim() : "";
  return v.length > 0 ? v : null;
};

/** Rows saved before the `@Filters` switch stored one list per named filter
 *  parameter (`PayorTypeSystemIDs`, …) rather than per config filter key. Each
 *  old name (and its historical aliases, lower-cased) → the config key(s) it
 *  now lives under, so a dashboard still reopens on a selection saved that way. */
const LEGACY_SAVED_KEYS: Record<string, string[]> = (() => {
  const aliases: Record<LegacyFilterParam, string[]> = {
    branchSystemIDs: ["branches", "branchids"],
    payorTypeSystemIDs: ["payorsystemids", "payors"],
    disciplines: [],
    jobTitles: [],
    referralSubTypes: ["sourcetypes", "referralsourcetypes"],
    referralSourceNames: ["referralsources"],
    repRosterSystemIDs: ["reps", "repsystemids"],
  };
  const out: Record<string, string[]> = {};
  for (const [key, param] of Object.entries(LEGACY_FILTER_PARAMS)) {
    for (const name of [param, ...aliases[param]]) {
      const n = name.toLowerCase();
      (out[n] ??= []).push(key);
    }
  }
  return out;
})();

const DATE_KEYS = {
  start: ["startdate", "fromdate"],
  end: ["enddate", "todate"],
};

/** Parse a `PerfCode = 'UserFilter'` row's `PerfDetails` into the filter
 *  selections to apply, keyed by config filter key. Null when the cell is
 *  unreadable.
 *
 *  The current shape is the `@Filters` map itself (`{ "payor": ["…"] }`), so a
 *  key is taken as-is; an old named-parameter key is mapped to its config key.
 *  An exact config key wins over a legacy name for the same filter. */
function parseFilterDetails(raw: unknown): SavedDashboardFilters | null {
  const d = detailsObject(raw);
  if (!d) return null;
  const values: Record<string, string[]> = {};
  const legacy: Record<string, string[]> = {};
  let startDate: string | null = null;
  let endDate: string | null = null;
  for (const [k, v] of Object.entries(d)) {
    const lk = k.toLowerCase();
    if (DATE_KEYS.start.includes(lk)) {
      startDate = text(v);
      continue;
    }
    if (DATE_KEYS.end.includes(lk)) {
      endDate = text(v);
      continue;
    }
    const list = valueList(v);
    if (!list.length) continue;
    const mapped = LEGACY_SAVED_KEYS[lk];
    if (mapped) {
      for (const key of mapped) legacy[key] = list;
    } else {
      values[k] = list;
    }
  }
  return { values: { ...legacy, ...values }, startDate, endDate };
}

/** True for the row kind that carries filters. Anything else — including a row
 *  with no `PerfCode` at all — is treated as a column layout, which is what the
 *  SP returned before filters existed. */
const isFilterRow = (r: CustomViewRow): boolean =>
  String(r.PerfCode ?? "").trim().toLowerCase() === USER_FILTER_PERF_CODE.toLowerCase();

/** The idle value — what a consumer outside a `DashboardPerfsProvider` sees,
 *  and its cue to fetch for itself. */
export const EMPTY_PERFS: DashboardPerfs = {
  views: null,
  filters: null,
  loading: false,
  error: null,
  ready: false,
  refetch: () => {},
};

/** The parsed halves of one `custom-views` response. */
interface ParsedPerfs {
  views: SavedCustomView[];
  filters: SavedDashboardFilters | null;
}

/**
 * Reads every saved preference for one dashboard via
 * `POST /dashboard/custom-views`. This is the FIRST call a dashboard makes —
 * its filters decide the parameters of everything that follows.
 *
 * `params: null` keeps the hook idle; the caller passes null until it knows the
 * dashboard id.
 *
 * CACHE (tier B, `staleTime: Infinity`): this app is the only writer, so the
 * explicit invalidation in `useSaveCustomView` / `useDeleteCustomView` is
 * complete. The practical effect is that revisiting a dashboard resolves
 * `ready` synchronously from cache, so `filtersApplied` flips on the first
 * render and the metrics request goes out immediately — removing a full
 * round-trip from the critical path of every dashboard switch after the first.
 */
export function useDashboardPerfs(params: GetCustomViewsParams | null): DashboardPerfs {
  const userKey = useUserKey();
  const wantedDash = params ? dashKey(params.dashboardID) : null;

  // Parse in `select` so it runs once per response rather than on every render.
  // `useCallback` keeps the identity stable, which is what lets TanStack memoize
  // the result.
  const select = useCallback(
    (raw: unknown): ParsedPerfs => {
      const rows = unwrapRows<CustomViewRow>(raw)
        // The SP reads its `@DashboardID` as "no filter" rather than a scope,
        // so it hands back every row this user has saved whatever was asked
        // for. Unscoped, a dashboard's starred By-Branch view would arrive on
        // All Metrics and — being `isDefault` — silently narrow it to that
        // dashboard's columns, and one dashboard's saved filters would be
        // applied on another. Keep only the rows saved against the scope the
        // caller actually asked for.
        .filter((r) => rowDash(r) === wantedDash);

      const views = rows
        .filter((r) => !isFilterRow(r))
        .map((r) => ({
          // Column aliases vary; accept the usual spellings so the delete call
          // still has an id to work with.
          id: (r.AppUserPerfID ?? r.AppUserPerfId ?? r.PerfID ?? r.PerfId ?? null) as
            | number
            | string
            | null,
          name: String(r.PerfName ?? "").trim(),
          details: parseViewDetails(r.PerfDetails),
        }))
        .filter((v): v is SavedCustomView => !!v.name && v.details !== null);

      // Last row wins: one filter row per dashboard is what the save writes,
      // and if the SP ever returns more than one the newest is last.
      const filterRow = rows.filter(isFilterRow).pop();
      const filters = filterRow ? parseFilterDetails(filterRow.PerfDetails) : null;

      return { views, filters };
    },
    [wantedDash],
  );

  const { data, loading, error, refetch, query } = useApiQuery<unknown, ParsedPerfs>({
    // Idle parks on a dedicated key rather than `perfs(u, null)`, which is All
    // Metrics' REAL scope — see `qk.perfsIdle`.
    queryKey: params ? qk.perfs(userKey, params.dashboardID) : qk.perfsIdle(userKey),
    url: getCustomViewsUrl,
    body: params,
    enabled: params != null,
    fallbackMessage: "Failed to fetch saved views",
    select,
    ...cacheTiers.prefs,
  });

  return {
    views: data?.views ?? null,
    filters: data?.filters ?? null,
    loading,
    error,
    // Settled either way: the dashboard must not wait forever on a failed
    // preference call — it just loads with its default filters.
    //
    // `!query.isFetching` matters as much as `isFetched` here. Because this
    // query always revalidates (`staleTime: 0`), reopening a dashboard renders
    // the PREVIOUS response from cache while the fresh one is in flight — and
    // `isFetched` is already true for that cached copy. Without the second
    // condition the page would seed its filter bar from the stale copy, lock it
    // in via its one-shot `filtersApplied`, and fire the metric calls with
    // filters the server may have since replaced. Waiting for the in-flight
    // fetch to settle keeps the original guarantee: the FIRST metrics request a
    // dashboard makes already carries its saved filters.
    ready: params != null && query.isFetched && !query.isFetching,
    refetch,
  };
}
